import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import ProductForm from '../components/ProductForm';
import { toast } from 'react-hot-toast';

const GET_PRODUCT = gql`
  query GetProduct($id: ID!) {
    product(id: $id) {
      id
      title
      description
      imageUrl
      basePrice
      serviceTypeId
      variations {
        id
        name
        price
      }
      isActive
      availableFromMonth
      availableFromDay
      availableToMonth
      availableToDay
      isCurrentlyAvailable
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(updateProductInput: $input) {
      id
      title
      description
      imageUrl
      basePrice
      serviceTypeId
      variations {
        id
        name
        price
      }
      isActive
      availableFromMonth
      availableFromDay
      availableToMonth
      availableToDay
      isCurrentlyAvailable
    }
  }
`;

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  
  const { loading: loadingProduct, data, error: queryError } = useQuery(GET_PRODUCT, {
    variables: { id },
    skip: !id
  });

  const [updateProduct, { loading: updating }] = useMutation(UPDATE_PRODUCT);

  const handleSubmit = async (formData) => {
    if (!id) return;

    try {
      setError(null);

      // For existing variations, keep the id. For new variations, exclude id
      const variations = formData.variations?.map(variation => {
        if (variation.id && data.product.variations.some(v => v.id === variation.id)) {
          // Existing variation - keep id
          return variation;
        }
        // New variation - exclude id
        const { id: _, ...newVariation } = variation;
        return newVariation;
      });

      await updateProduct({
        variables: {
          input: {
            id,
            ...formData,
            availableFromMonth: formData.availableFromMonth || null,
            availableFromDay: formData.availableFromDay || null,
            availableToMonth: formData.availableToMonth || null,
            availableToDay: formData.availableToDay || null,
            serviceTypeId: formData.serviceTypeId || null,
            variations
          }
        }
      });

      toast.success('Product updated successfully');
      navigate('/dashboard/products');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while updating the product';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (loadingProduct) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Error loading product: {queryError.message}
        </div>
      </div>
    );
  }

  if (!data?.product) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Product not found.
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
          {error}
        </div>
      )}
      <ProductForm
        initialData={data.product}
        onSubmit={handleSubmit}
        isLoading={updating}
      />
    </>
  );
} 